import * as THREE from 'three';
import { settingsStore, type GraphicsQuality } from '../../core/settings';

// ---------------------------------------------------------------------------
// Height-Based Fog System
// Replaces uniform FogExp2 with distance + height-based exponential fog,
// optionally with FBM noise for drifting patches.
// Patches materials via onBeforeCompile to inject custom fog logic.
// ---------------------------------------------------------------------------

export interface HeightFogParams {
  density: number;
  heightFalloff: number;
  fogColor: THREE.Color;
  enableNoise: boolean;
}

const DEFAULT_PARAMS: HeightFogParams = {
  density: 0.008,
  heightFalloff: 0.15,
  fogColor: new THREE.Color(0xc8bfa0), // warm desert fog
  enableNoise: false,
};

// Custom fog GLSL chunks injected via onBeforeCompile
const HEIGHT_FOG_PARS_FRAGMENT = /* glsl */ `
  uniform float heightFogDensity;
  uniform float heightFogFalloff;
  uniform vec3 heightFogColor;
  uniform vec3 heightFogCameraPos;
  uniform float heightFogTime;
  uniform float heightFogNoiseEnabled;
  varying vec3 vHeightFogWorldPos;
`;

const HEIGHT_FOG_PARS_VERTEX = /* glsl */ `
  varying vec3 vHeightFogWorldPos;
`;

const HEIGHT_FOG_VERTEX = /* glsl */ `
  vHeightFogWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
`;

// FBM noise for drifting fog patches
const HEIGHT_FOG_NOISE_GLSL = /* glsl */ `
  float hfHash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }
  float hfNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hfHash(i);
    float b = hfHash(i + vec2(1.0, 0.0));
    float c = hfHash(i + vec2(0.0, 1.0));
    float d = hfHash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  float hfFbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      v += a * hfNoise(p);
      p *= 2.0;
      a *= 0.5;
    }
    return v;
  }
`;

const HEIGHT_FOG_FRAGMENT = /* glsl */ `
  {
    float fogDepth = length(vHeightFogWorldPos - heightFogCameraPos);
    vec3 fogDir = normalize(vHeightFogWorldPos - heightFogCameraPos);
    float heightFactor = exp(-heightFogCameraPos.y * heightFogFalloff);
    float fogDirY = max(fogDir.y, 0.001);
    float fogAmount = heightFactor * (1.0 - exp(-fogDepth * fogDirY * heightFogDensity)) / fogDirY;
    fogAmount = clamp(fogAmount, 0.0, 1.0);

    // Optional FBM noise for drifting patches
    if (heightFogNoiseEnabled > 0.5) {
      float noiseVal = hfFbm(vHeightFogWorldPos.xz * 0.02 + heightFogTime * 0.1);
      fogAmount *= mix(0.7, 1.3, noiseVal);
      fogAmount = clamp(fogAmount, 0.0, 1.0);
    }

    gl_FragColor.rgb = mix(gl_FragColor.rgb, heightFogColor, fogAmount);
  }
`;

// Cache key suffix to avoid duplicate shader compilations
const CACHE_KEY_BASE = 'height-fog-v1-noise-';

/**
 * CPU-side estimate of the height fog amount for a given fragment.
 * Mirrors the GLSL calculation — useful for gameplay logic (visibility checks)
 * and testing.
 */
export function estimateHeightFogAmount(
  distance: number,
  cameraY: number,
  _fragY: number,
  density: number,
  heightFalloff: number,
): number {
  const heightFactor = Math.exp(-cameraY * heightFalloff);
  // Compute fogDirY from fragment height relative to camera, mirroring the shader:
  //   fogDir = normalize(fragPos - cameraPos) → fogDir.y = (fragY - cameraY) / distance
  const fogDirY = Math.max(0.001, Math.abs(_fragY - cameraY) / Math.max(distance, 0.001));
  const fogAmount = heightFactor * (1 - Math.exp(-distance * fogDirY * density)) / fogDirY;
  return Math.max(0, Math.min(1, fogAmount));
}

export class HeightFogSystem {
  private scene: THREE.Scene;
  private params: HeightFogParams;
  private enabled = false;

  // Shared uniforms — updated once per frame, referenced by all patched materials
  private uniforms = {
    heightFogDensity: { value: DEFAULT_PARAMS.density },
    heightFogFalloff: { value: DEFAULT_PARAMS.heightFalloff },
    heightFogColor: { value: DEFAULT_PARAMS.fogColor.clone() },
    heightFogCameraPos: { value: new THREE.Vector3() },
    heightFogTime: { value: 0.0 },
    heightFogNoiseEnabled: { value: 0.0 },
  };

  // Track patched materials and their original hooks so we can restore on disable
  private patchedMaterials = new Set<THREE.Material>();
  private originalHooks = new Map<THREE.Material, {
    onBeforeCompile: THREE.Material['onBeforeCompile'];
    customProgramCacheKey: (() => string) | undefined;
  }>();

  constructor(scene: THREE.Scene, params?: Partial<HeightFogParams>) {
    this.scene = scene;
    this.params = { ...DEFAULT_PARAMS, ...params };
    if (params?.fogColor) {
      this.params.fogColor = params.fogColor.clone();
    }
    this.syncUniforms();
  }

  /** Enable height fog — patches all current scene materials and disables scene.fog. */
  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    // Disable built-in Three.js fog
    this.scene.fog = null;
    this.patchAllMaterials();
  }

  /** Disable height fog — un-patches materials. Re-enables FogExp2 as fallback. */
  disable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    // Restore built-in fog
    this.scene.fog = new THREE.FogExp2(0xc8bfa0, 0.006);
    // Restore original hooks saved during patching
    for (const mat of this.patchedMaterials) {
      const orig = this.originalHooks.get(mat);
      if (orig) {
        mat.onBeforeCompile = orig.onBeforeCompile;
        (mat as any).customProgramCacheKey = orig.customProgramCacheKey;
      } else {
        mat.onBeforeCompile = () => {};
        (mat as any).customProgramCacheKey = undefined;
      }
      mat.needsUpdate = true;
    }
    this.patchedMaterials.clear();
    this.originalHooks.clear();
  }

  /** Update parameters. */
  setParams(params: Partial<HeightFogParams>): void {
    Object.assign(this.params, params);
    if (params.fogColor) {
      this.params.fogColor = params.fogColor instanceof THREE.Color
        ? params.fogColor.clone()
        : this.params.fogColor;
    }
    this.syncUniforms();

    // If noise state changed, re-patch all materials (cache key depends on it)
    if ('enableNoise' in params && this.enabled) {
      for (const mat of this.patchedMaterials) {
        mat.needsUpdate = true;
      }
    }
  }

  /** Set quality — determines noise and enable/disable. */
  setQuality(quality: GraphicsQuality): void {
    if (quality === 'low') {
      this.disable();
      return;
    }
    const noise = quality === 'high' || quality === 'ultra';
    this.setParams({ enableNoise: noise });
    this.enable();
    // Re-patch any new materials that might have loaded
    if (this.enabled) {
      this.patchAllMaterials();
    }
  }

  /** Call once per frame — only updates shared uniforms (no scene traversal). */
  update(dt: number, cameraPosition: THREE.Vector3): void {
    if (!this.enabled) return;
    if (!this.uniforms?.heightFogCameraPos) return;
    this.uniforms.heightFogCameraPos.value.copy(cameraPosition);
    this.uniforms.heightFogTime.value += dt;
  }

  /** Patch any newly added materials (e.g., after loading props or dynamic objects). */
  patchNewMaterials(): void {
    if (!this.enabled) return;
    this.patchAllMaterials();
  }

  dispose(): void {
    this.disable();
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private syncUniforms(): void {
    if (!this.uniforms?.heightFogDensity) return;
    this.uniforms.heightFogDensity.value = this.params.density;
    this.uniforms.heightFogFalloff.value = this.params.heightFalloff;
    this.uniforms.heightFogColor.value.copy(this.params.fogColor);
    this.uniforms.heightFogNoiseEnabled.value = this.params.enableNoise ? 1.0 : 0.0;
  }

  private patchAllMaterials(): void {
    this.scene.traverse((obj: THREE.Object3D) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of materials) {
        if (!mat) continue;
        // Only patch PBR / Phong materials
        if (!(mat instanceof THREE.MeshStandardMaterial) && !(mat instanceof THREE.MeshPhongMaterial)) continue;
        // Skip already patched
        if (this.patchedMaterials.has(mat)) continue;
        // Skip effect materials
        if ((obj.userData as any)?.isEffect) continue;
        this.patchMaterial(mat);
      }
    });
  }

  private patchMaterial(material: THREE.Material): void {
    const uniforms = this.uniforms;
    const noiseEnabled = this.params.enableNoise;

    // Save original hooks for restore on disable
    const existingCompile = material.onBeforeCompile.bind(material);
    const existingCacheKeyRaw = typeof (material as any).customProgramCacheKey === 'function'
      ? ((material as any).customProgramCacheKey as () => string).bind(material)
      : undefined;
    this.originalHooks.set(material, {
      onBeforeCompile: material.onBeforeCompile,
      customProgramCacheKey: existingCacheKeyRaw,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    material.onBeforeCompile = (shader: any, renderer: THREE.WebGLRenderer) => {
      try {
        // Call original hook first so it can modify the shader
        if (existingCompile) {
          existingCompile.call(material, shader, renderer);
        }
        // Inject uniforms
        shader.uniforms['heightFogDensity'] = uniforms.heightFogDensity;
        shader.uniforms['heightFogFalloff'] = uniforms.heightFogFalloff;
        shader.uniforms['heightFogColor'] = uniforms.heightFogColor;
        shader.uniforms['heightFogCameraPos'] = uniforms.heightFogCameraPos;
        shader.uniforms['heightFogTime'] = uniforms.heightFogTime;
        shader.uniforms['heightFogNoiseEnabled'] = uniforms.heightFogNoiseEnabled;

        // Vertex: add world position varying
        shader.vertexShader = shader.vertexShader.replace(
          '#include <common>',
          '#include <common>\n' + HEIGHT_FOG_PARS_VERTEX
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n' + HEIGHT_FOG_VERTEX
        );

        // Fragment: add fog uniforms + varying
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <common>',
          '#include <common>\n' + HEIGHT_FOG_PARS_FRAGMENT +
          (noiseEnabled ? '\n' + HEIGHT_FOG_NOISE_GLSL : '')
        );

        // Replace Three.js fog application with our height fog
        // The fog_fragment include applies fog at the end — we replace it entirely
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <fog_fragment>',
          HEIGHT_FOG_FRAGMENT
        );
      } catch (err) {
        console.warn('[HeightFog] Shader patching failed for material:', material.name || material.uuid, err);
      }
    };

    // Custom cache key — compose with saved original key (not the current one which may be ours)
    const baseCacheKeyFn = existingCacheKeyRaw ?? (() => '');
    const fogKey = CACHE_KEY_BASE + (noiseEnabled ? '1' : '0');
    (material as any).customProgramCacheKey = () => baseCacheKeyFn() + '_' + fogKey;
    material.needsUpdate = true;

    this.patchedMaterials.add(material);
  }
}
